import {
  CATEGORIES_OPTIONS_IDS,
  CategoryOptionId,
} from "@max/common/dist/setfan";
import { CATEGORIES_OPTIONS } from "Routes/SetFan/constants/profileCategories";
import { FieldArray, Form, Formik } from "formik";
import { Button } from "melodies-source/Button";
import { Modal } from "melodies-source/Modal";
import { Body1 } from "melodies-source/Text";
import { useMemo } from "react";
import styled from "styled-components";
import * as yup from "yup";
import { SelectionCard } from "../SelectionCard";
import { SvgAudience } from "melodies-source/Svgs/Audience";

const Description = styled(Body1)`
  margin: 12px 0 20px 0;
`;

const OptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 22px;
  gap: 20px;
`;

const Save = styled(Button)`
  min-width: 156px;
`;

interface Values {
  categoryOptionIds: CategoryOptionId[];
}

interface EditCategoriesFormProps {
  initialValues?: CategoryOptionId[];
  numberOfCategories: number;
  onCancel?: () => void;
  onSave?: (values: CategoryOptionId[]) => void;
}

const EditCategoriesForm = ({
  initialValues = [...CATEGORIES_OPTIONS_IDS],
  numberOfCategories,
  onCancel,
  onSave,
}: EditCategoriesFormProps) => {
  const plural = numberOfCategories !== 1;

  const validationSchema = yup.object().shape({
    categoryOptionIds: yup
      .array()
      .of(yup.string())
      .min(
        numberOfCategories - 1,
        `Select at least ${numberOfCategories} ${
          plural ? "categories" : "category"
        }.`,
      ),
  });

  return (
    <Formik<Values>
      initialValues={{ categoryOptionIds: initialValues }}
      validationSchema={validationSchema}
      onSubmit={({ categoryOptionIds }) => onSave(categoryOptionIds)}
    >
      {({ values, touched, errors }) => (
        <Form>
          <Description>
            Your fans will see up to {numberOfCategories}{" "}
            {plural ? "categories" : "category"} of questions.{" "}
            {plural ? "These categories" : "This category"} will be
            intelligently chosen from this list. Please uncheck any that you
            don't want shown:
          </Description>
          <FieldArray name="categoryOptionIds">
            {({ push, remove }) => (
              <OptionList>
                <SelectionCard
                  header="Fan Contact Info"
                  subHeader="Gather the names, locations, and phone numbers of your supporters."
                  icon={<SvgAudience width={35} height={35} />}
                  locked
                  selected
                />
                {CATEGORIES_OPTIONS.map((option) => (
                  <SelectionCard
                    key={option.id}
                    header={option.header}
                    subHeader={option.subHeader}
                    icon={option.icon}
                    selected={values.categoryOptionIds.includes(option.id)}
                    onClick={() =>
                      values.categoryOptionIds.includes(option.id)
                        ? remove(values.categoryOptionIds.indexOf(option.id))
                        : push(option.id)
                    }
                  />
                ))}
              </OptionList>
            )}
          </FieldArray>
          {touched.categoryOptionIds && errors.categoryOptionIds && (
            <Body1 style={{ color: "#FF0000", marginTop: 16 }}>
              {errors.categoryOptionIds}
            </Body1>
          )}
          <Footer>
            <Button type="button" text={true} onClick={() => onCancel?.()}>
              Cancel
            </Button>
            <Save type="submit">Save</Save>
          </Footer>
        </Form>
      )}
    </Formik>
  );
};

interface EditCategoriesDisplayedProps {
  initialValues?: CategoryOptionId[];
  numberOfCategories: number;
  isOpen?: boolean;
  onClose?: () => void;
  onSave?: (values: CategoryOptionId[]) => void;
}

export const EditCategoriesDisplayed = ({
  initialValues,
  numberOfCategories,
  isOpen,
  onClose,
  onSave,
}: EditCategoriesDisplayedProps) => {
  return (
    <Modal
      variant="large"
      header="Edit Categories Displayed"
      isOpen={isOpen}
      onClose={onClose}
      withBackdropClose={false}
    >
      <EditCategoriesForm
        numberOfCategories={numberOfCategories}
        initialValues={initialValues}
        onCancel={onClose}
        onSave={onSave}
      />
    </Modal>
  );
};
